import React, { useState, useCallback } from 'react';
import { FullPageLoader, PageContentLayout } from '@tassomutuo-backoffice/ui';
import {
  Box,
  Tab,
  Tabs,
  Paper,
  Button,
  Alert,
  AlertTitle,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Task } from '@tassomutuo-backoffice/types';
import { toast } from 'react-toastify';
import CalendarView from './components/CalendarView';
import ListView from './components/ListView';
import { TaskDetailModal, AddTaskModal } from '../../components';
import {
  getTasks,
  updateTask,
  UpdateTaskParams,
  deleteTask,
} from '../../services/api/tasks/tasks';
import {
  getGoogleCalendarAuthUrl,
  getGoogleCalendarStatus,
  getGoogleCalendarEvents,
  GoogleCalendarEvent,
} from '../../services/api/google-calendar/google-calendar';
import { getHsOwnerIdInLocalStorage } from '../../services/localStorage/localStorage';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import AddIcon from '@mui/icons-material/Add';
import { startOfDay } from 'date-fns';
import { selectUser, useUserStore } from '../../store/user';
import { useSearchParams, useNavigate } from 'react-router-dom';

type ViewType = 'calendar' | 'list';

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;

const TasksPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useUserStore(selectUser);
  const userGCalendarAuthorized = !!user?.googleCalendar?.authorized;

  // Get view from URL params, default to 'calendar'
  const urlView = searchParams.get('tab') as ViewType;
  const [view, setView] = useState<ViewType>(
    urlView === 'list' || urlView === 'calendar' ? urlView : 'calendar'
  );
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [isCheckingCalendar, setIsCheckingCalendar] = useState(false);

  // State for date range - for calendar view
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date } | null>(
    null
  );

  // State for pagination - for list view
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  // Handle calendar range change
  const handleCalendarRangeChange = useCallback(
    (range: { start: Date; end: Date }) => {
      setDateRange(range);
    },
    []
  );

  // Fetch tasks based on current view
  const {
    data: tasksData,
    isLoading,
    error,
  } = useQuery({
    queryKey:
      view === 'calendar'
        ? ['tasks', 'calendar', dateRange]
        : ['tasks', 'list', paginationModel],
    queryFn: () => {
      if (view === 'calendar' && dateRange) {
        // Calendar view: fetch tasks for the visible date range
        return getTasks({
          dateFrom: dateRange.start.toISOString(),
          dateTo: dateRange.end.toISOString(),
          sortBy: 'date:asc',
        });
      } else {
        // List view: fetch tasks with pagination from today onwards
        return getTasks({
          dateFrom: startOfDay(new Date()).toISOString(),
          sortBy: 'date:asc',
          limit: paginationModel.pageSize,
          page: paginationModel.page + 1, // API pages are 1-indexed
        });
      }
    },
    enabled: view === 'list' || (view === 'calendar' && dateRange !== null),
    refetchOnWindowFocus: false,
  });

  const tasks = tasksData?.results || [];
  const totalRows = tasksData?.totalResults || 0;

  // Fetch Google Calendar events based on current view
  const { data: googleEventsData } = useQuery({
    queryKey: ['googleCalendarEvents', view, dateRange],
    queryFn: () => {
      if (view === 'calendar' && dateRange) {
        return getGoogleCalendarEvents({
          startDate: dateRange.start.toISOString(),
          endDate: dateRange.end.toISOString(),
        });
      }
      // Don't fetch Google events for list view
      return Promise.resolve({ events: [], count: 0 });
    },
    enabled:
      userGCalendarAuthorized && view === 'calendar' && dateRange !== null,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const googleEvents = googleEventsData?.events || [];

  // Get the current selected task from the fresh data
  const selectedTask = selectedTaskId
    ? tasks.find((t) => t.id === selectedTaskId) || null
    : null;

  // Update task mutation
  const updateTaskMutation = useMutation({
    mutationFn: ({
      taskId,
      updateData,
    }: {
      taskId: string;
      updateData: UpdateTaskParams;
    }) => updateTask(taskId, updateData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Promemoria aggiornato con successo');
    },
    onError: (error: Error) => {
      toast.error(
        error?.message || "Errore durante l'aggiornamento del promemoria"
      );
      console.error('Error updating task:', error);
    },
  });

  // Delete task mutation
  const deleteTaskMutation = useMutation({
    mutationFn: (taskId: string) => deleteTask(taskId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Promemoria eliminato con successo');
      setIsDetailModalOpen(false);
      setSelectedTaskId(null);
    },
    onError: (error: Error) => {
      toast.error(
        error?.message || "Errore durante l'eliminazione del promemoria"
      );
      console.error('Error deleting task:', error);
    },
  });

  const handleTaskDateChange = (taskId: string, newDate: Date) => {
    updateTaskMutation.mutate({
      taskId,
      updateData: {
        date: newDate.toISOString(),
      },
    });
  };

  const handleTaskToggle = (taskId: string, completed: boolean) => {
    updateTaskMutation.mutate({
      taskId,
      updateData: {
        completed,
      },
    });
  };

  const handleTaskEdit = (task: Task) => {
    setSelectedTaskId(task.id);
    setIsDetailModalOpen(true);
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTaskId(task.id);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedTaskId(null);
  };

  const handleDeleteTask = (taskId: string) => {
    deleteTaskMutation.mutate(taskId);
  };

  const handleUpdateTaskFromModal = (
    taskId: string,
    data: {
      action?: string;
      notes?: string;
      date?: string;
      completed?: boolean;
    }
  ) => {
    updateTaskMutation.mutate({
      taskId,
      updateData: data,
    });
  };

  const handleSelectSlot = (_slotInfo: { start: Date; end: Date }) => {
    //  setSelectedDate(slotInfo.start);
    //  setIsAddModalOpen(true);
  };

  const handleGoogleEventClick = (event: GoogleCalendarEvent) => {
    // Open the event in a new tab
    if (event.htmlLink) {
      window.open(event.htmlLink, '_blank');
    }
  };

  const handleViewChange = (
    _event: React.SyntheticEvent,
    newValue: ViewType
  ) => {
    setView(newValue);
    // Update URL without the tab param if it's the default view (calendar)
    if (newValue === 'calendar') {
      navigate('/tasks', { replace: true });
    } else {
      navigate(`/tasks?tab=${newValue}`, { replace: true });
    }
  };

  const handleOpenAddTaskModal = async () => {
    try {
      setIsCheckingCalendar(true);

      // Verifica se si sta impersonando un altro utente
      const isImpersonating = !!getHsOwnerIdInLocalStorage();

      if (isImpersonating && user?.role !== 'admin') {
        toast.warning('Non puoi creare promemoria per i tuoi membri', {
          autoClose: 5000,
        });
        return;
      }

      // Verifica lo stato di Google Calendar
      const calendarStatus = await getGoogleCalendarStatus();

      if (calendarStatus.connected) {
        // Se connesso, apri il modal direttamente
        setIsAddTaskModalOpen(true);
      } else {
        // Se non connesso
        if (isImpersonating) {
          // Durante l'impersonazione, non è possibile connettere Google Calendar
          // perché richiederebbe l'autenticazione dell'utente impersonato
          toast.warning(
            'Google Calendar non connesso per questo utente. Il promemoria verrà creato senza sincronizzazione con Google Calendar.',
            { autoClose: 5000 }
          );
          // Apri comunque il modal per permettere la creazione del task
          setIsAddTaskModalOpen(true);
        } else {
          // Se non si sta impersonando, avvia il flusso OAuth normale
          await handleConnectCalendar();
        }
      }
    } catch (error) {
      // In caso di errore nel controllo, mostra un messaggio
      console.error('Error checking calendar status:', error);
      toast.error(
        'Impossibile verificare lo stato di Google Calendar. Riprova.'
      );
    } finally {
      setIsCheckingCalendar(false);
    }
  };

  const handleConnectCalendar = async () => {
    try {
      // Ottieni l'URL di autorizzazione
      const { authUrl } = await getGoogleCalendarAuthUrl();

      // Calcola la posizione centrale per il popup
      const left = window.screen.width / 2 - POPUP_WIDTH / 2;
      const top = window.screen.height / 2 - POPUP_HEIGHT / 2;

      // Apri il popup
      const popup = window.open(
        authUrl,
        'GoogleCalendarAuth',
        `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no`
      );

      if (!popup) {
        toast.error('Popup bloccato. Abilita i popup per questo sito.');
        return;
      }

      // Monitora la chiusura del popup
      const popupCheckInterval = setInterval(() => {
        if (popup.closed) {
          clearInterval(popupCheckInterval);
        }
      }, 500);

      // Ascolta messaggi dal popup
      const messageHandler = (event: MessageEvent) => {
        // Verifica che il messaggio provenga dal dominio corretto
        if (event.origin !== window.location.origin) return;

        if (event.data?.type === 'GOOGLE_CALENDAR_AUTH_SUCCESS') {
          popup.close();
          toast.success('Google Calendar connesso con successo!');
          // Apri il modal di creazione task dopo il successo
          setIsAddTaskModalOpen(true);
          window.removeEventListener('message', messageHandler);
          window.location.reload();
        } else if (event.data?.type === 'GOOGLE_CALENDAR_AUTH_ERROR') {
          popup.close();
          toast.error('Errore durante la connessione a Google Calendar');
          window.removeEventListener('message', messageHandler);
        }
      };

      window.addEventListener('message', messageHandler);

      // Cleanup dopo 5 minuti (timeout)
      setTimeout(() => {
        if (popup && !popup.closed) {
          popup.close();
        }
        window.removeEventListener('message', messageHandler);
      }, 5 * 60 * 1000);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Errore durante la connessione a Google Calendar';
      toast.error(errorMessage);
    }
  };

  const handleCloseAddTaskModal = () => {
    setIsAddTaskModalOpen(false);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ backgroundColor: '#E9EAEB', minHeight: '100vh', pt: 4 }}>
        <FullPageLoader
          show={isLoading || updateTaskMutation.isPending || isCheckingCalendar}
        />

        <PageContentLayout
          title="Promemoria"
          error={error}
          containerSx={{ pt: 0 }}
        >
          {!userGCalendarAuthorized && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleConnectCalendar}
                >
                  Connetti ora
                </Button>
              }
            >
              <AlertTitle>Google Calendar non connesso</AlertTitle>
              Connetti il tuo account Google Calendar per sincronizzare
              automaticamente i tuoi promemoria e visualizzare i tuoi eventi.
            </Alert>
          )}

          <Box
            sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}
          >
            <Paper elevation={0} sx={{ flex: 1 }}>
              <Tabs
                value={view}
                onChange={handleViewChange}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab label="Calendario" value="calendar" />
                <Tab label="Lista" value="list" />
              </Tabs>
            </Paper>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenAddTaskModal}
              disabled={isCheckingCalendar}
              sx={{ height: 48 }}
            >
              {isCheckingCalendar
                ? 'Controllo in corso...'
                : 'Aggiungi promemoria'}
            </Button>
          </Box>

          {view === 'calendar' ? (
            <CalendarView
              tasks={tasks}
              googleEvents={googleEvents}
              onTaskDateChange={handleTaskDateChange}
              onSelectEvent={handleTaskClick}
              onSelectGoogleEvent={handleGoogleEventClick}
              onSelectSlot={handleSelectSlot}
              onRangeChange={handleCalendarRangeChange}
            />
          ) : (
            <ListView
              tasks={tasks}
              loading={isLoading}
              rowCount={totalRows}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              onTaskToggle={handleTaskToggle}
              onTaskEdit={handleTaskEdit}
              onTaskClick={handleTaskClick}
            />
          )}

          <TaskDetailModal
            open={isDetailModalOpen}
            task={selectedTask}
            onClose={handleCloseDetailModal}
            onDelete={handleDeleteTask}
            onUpdate={handleUpdateTaskFromModal}
            isDeleting={deleteTaskMutation.isPending}
            isUpdating={updateTaskMutation.isPending}
            currentView={view}
          />

          <AddTaskModal
            open={isAddTaskModalOpen}
            onClose={handleCloseAddTaskModal}
          />
        </PageContentLayout>
      </Box>
    </DndProvider>
  );
};

export default TasksPage;
